import React, { createContext, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useTranslation } from 'react-i18next';
import { updateUserLanguage } from '../services/userService';

const LanguageContext = createContext();

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export function LanguageProvider({ children }) {
  const { currentUser, userProfile } = useAuth();
  const { i18n } = useTranslation();
  const availableLanguages = ['en', 'sl'];

  // Honor a ?lang= query param on first load (used by hreflang / shared links).
  useEffect(() => {
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if (urlLang && availableLanguages.includes(urlLang)) {
      i18n.changeLanguage(urlLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load user's language preference when profile loads
  useEffect(() => {
    if (userProfile?.language) {
      i18n.changeLanguage(userProfile.language);
    }
  }, [userProfile, i18n]);

  const changeLanguage = async (languageCode) => {
    try {
      // Change language in i18n
      await i18n.changeLanguage(languageCode);

      // Persist to Firestore if user is logged in
      if (currentUser) {
        await updateUserLanguage(currentUser.uid, languageCode);
      }
    } catch (error) {
      console.error('Error changing language:', error);
      throw error;
    }
  };

  const value = {
    currentLanguage: i18n.language,
    changeLanguage,
    availableLanguages
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}